/** The result of a create: the new invites with their links — Copy and QR per link, Copy all,
 *  and Print QR sheet (D14). The links come from the 201 and live in this dialog's props only;
 *  once it closes, a link is shown again only through the audited `link` endpoint. */
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import PrintIcon from '@mui/icons-material/Print';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import {
  Alert,
  Box,
  Button,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { useState } from 'react';

import type { CreatedInvite, InviteCreateResponse } from '@/api/onboardingInvites';
import { AppDialog } from '@/components/AppDialog';
import { QrCodePopover } from '@/components/QrCodePopover';
import { useSnackbar } from '@/components/Snackbar';

import { printQrSheet } from './printQrSheet';

const mono = { fontFamily: 'monospace', fontSize: 12 } as const;

/** Why a send-now did not go out, in words. */
const SEND_OUTCOME: Record<string, string> = {
  'outbound-suppressed': 'outbound e-mail is suppressed in this environment',
  'outbound-guard-error': 'the outbound guard failed',
  'unsendable-recipient': 'the address cannot receive e-mail',
  error: 'the mail service failed',
};

export function sheetTitle(invites: CreatedInvite[]): string {
  const labels = [...new Set(invites.map((i) => i.label))];
  return labels.length === 1 ? `Invite links — ${labels[0]}` : 'Invite links';
}

interface Props {
  result: InviteCreateResponse | null;
  onClose: () => void;
}

export function InviteCreatedDialog({ result, onClose }: Props) {
  const snackbar = useSnackbar();
  const [qr, setQr] = useState<{ invite: CreatedInvite; anchor: HTMLElement } | null>(null);
  const invites = result?.invites ?? [];
  const first = invites[0];

  const copy = (text: string, what: string) => {
    void navigator.clipboard
      ?.writeText(text)
      .then(() => snackbar.show(`${what} copied.`, 'success'))
      .catch(() => snackbar.show('Copy failed — the clipboard is unavailable.', 'error'));
  };

  const print = () => {
    const ok = printQrSheet(
      invites.map((i) => ({ label: i.label, publicId: i.public_id, link: i.link })),
      sheetTitle(invites),
    );
    if (!ok) snackbar.show('The browser blocked the print window — allow pop-ups for the console and try again.', 'warning');
  };

  return (
    <AppDialog
      open={Boolean(result)}
      onClose={onClose}
      size="md"
      disableBackdropClose
      title={invites.length > 1 ? `${invites.length} invites created` : 'Invite created'}
      actions={
        <>
          {invites.length > 1 && (
            <Button startIcon={<ContentCopyIcon />} onClick={() => copy(invites.map((i) => i.link).join('\n'), 'Links')}>
              Copy all links
            </Button>
          )}
          <Button startIcon={<PrintIcon />} onClick={print}>
            Print QR sheet
          </Button>
          <Button variant="contained" onClick={onClose}>
            Done
          </Button>
        </>
      }
    >
      <Stack spacing={2} sx={{ mt: 1 }}>
        {result?.send &&
          (result.send.sent ? (
            <Alert severity="success">The link was e-mailed to {first?.email}.</Alert>
          ) : (
            <Alert severity="warning">
              The e-mail was not sent
              {result.send.outcome ? ` (${SEND_OUTCOME[result.send.outcome] ?? result.send.outcome})` : ''}. The link
              stays valid: copy it, or resend it from the grid.
            </Alert>
          ))}
        <Alert severity="info">
          Each link works until its invite is used — share it only with the prospect. This is the one time the links
          are shown without asking: later, <strong>Copy link</strong> and <strong>QR code</strong> fetch a link again,
          and each fetch is recorded.
        </Alert>
        {result?.batch_id && (
          <Typography variant="body2" color="text.secondary">
            Batch <Box component="span" sx={mono}>{result.batch_id}</Box> — search <Box component="code">batch={result.batch_id}</Box>{' '}
            to find these invites again.
          </Typography>
        )}
        <Table size="small" sx={{ '& td, & th': { px: 1 } }}>
          <TableHead>
            <TableRow>
              <TableCell>Invite</TableCell>
              <TableCell>Link</TableCell>
              <TableCell align="right" sx={{ width: 96 }} aria-label="Actions" />
            </TableRow>
          </TableHead>
          <TableBody>
            {invites.map((invite) => (
              <TableRow key={invite.id} data-invite-row={invite.id}>
                <TableCell sx={{ maxWidth: 220 }}>
                  <Typography variant="body2" noWrap title={invite.label}>
                    {invite.label}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={mono}>
                    #{invite.id} · {invite.public_id}
                  </Typography>
                </TableCell>
                <TableCell sx={{ maxWidth: 320 }}>
                  <Box component="code" sx={{ ...mono, display: 'block', overflowWrap: 'anywhere' }}>
                    {invite.link}
                  </Box>
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="Copy link">
                    <IconButton size="small" aria-label={`Copy link of invite ${invite.public_id}`} onClick={() => copy(invite.link, 'Link')}>
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="QR code">
                    <IconButton
                      size="small"
                      aria-label={`QR code of invite ${invite.public_id}`}
                      onClick={(e) => setQr({ invite, anchor: e.currentTarget })}
                    >
                      <QrCode2Icon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Stack>
      <QrCodePopover
        value={qr?.invite.link ?? null}
        anchorEl={qr?.anchor ?? null}
        onClose={() => setQr(null)}
        title={qr ? `QR code — ${qr.invite.label}` : ''}
        fileName={qr ? `invite-${qr.invite.public_id}.png` : 'invite.png'}
      />
    </AppDialog>
  );
}
